import { ApolloProvider } from '@apollo/client/react';
import { useMemo } from 'react';
import { BrowserRouter, NavLink, useMatch, useNavigate } from 'react-router-dom';

import { displayName } from '@/account/arcade-api';
import { usePerson } from '@/account/usePerson';
import { AuthProvider } from '@/auth/AuthProvider';
import { useAuth } from '@/auth/useAuth';
import { PageShell } from '@/components/PageShell/PageShell';
import { environmentName } from '@/config/runtime-config';
import { createApolloClient } from '@/lib/apollo-client';
import { AppRoutes } from '@/routes/AppRoutes';
import { ToastProvider } from '@/toast/ToastProvider';

const SECTIONS = [
  { to: '/', label: 'Usage', end: true },
  { to: '/playground', label: 'Playground', end: false },
];

function Shell() {
  const { status, signOut } = useAuth();
  const { person } = usePerson();
  const navigate = useNavigate();
  const environment = useMemo(() => environmentName(), []);
  const ready = status === 'authenticated' && person !== null;
  const jobView = useMatch('/playground/:id');

  return (
    <PageShell
      bare={!ready}
      textured={!jobView || jobView.params.id === 'new'}
      environment={environment}
      nav={
        ready
          ? SECTIONS.map(({ to, label, end }) => (
              <NavLink end={end} key={to} to={to}>
                {label}
              </NavLink>
            ))
          : null
      }
      onSettings={ready ? () => navigate('/settings') : undefined}
      onApiKeys={ready ? () => navigate('/settings/api-keys') : undefined}
      onSignOut={
        ready
          ? async () => {
              await signOut();
              navigate('/', { replace: true });
            }
          : undefined
      }
      signedInAs={person ? displayName(person) : null}
      signedInEmail={person?.email ?? null}
    >
      <AppRoutes />
    </PageShell>
  );
}

export function App() {
  // The client reads the runtime config at construction, so it is built once
  // rather than on every render.
  const client = useMemo(() => createApolloClient(), []);

  return (
    <ApolloProvider client={client}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ApolloProvider>
  );
}
